// The one page review summary a reviewer reads before deciding a step: the
// contract basis and why, the allegation, every gate with pass or fail, every
// figure with its source, the deductions with the cap and the floor applied, and
// who approved each step and when. Printable as it stands.
import { useEffect } from 'react'
import { ArrowLeft, Printer } from 'lucide-react'
import { useGetSeparation } from '../../hooks/backend/separations'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { BASIS_LABEL, LINE_LABEL, STATUS_LABEL, formatDay, formatWhen, formatZar, fullFigures, lineAmount, type Detail } from '../../utils/separation'

export function SeparationSummary({ id, onBack }: { id: string; onBack: () => void }) {
  const q = useGetSeparation<Detail>()
  const load = q.trigger
  useEffect(() => { void load({ id }, { skipCache: true }).catch(() => undefined) }, [load, id])
  const d = q.data
  if (q.error && !d) return <p className="p-6 text-sm text-rose-700 dark:text-rose-300">{q.error}</p>
  if (!d || d.case.id !== id) return <p className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading…</p>
  const c = d.case
  const f = d.figures
  const gates = c.status === 'preparing' ? d.issue_gates : d.sign_gates
  const basis = (c.basis ?? d.basis_live.basis) as keyof typeof BASIS_LABEL

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="mx-auto max-w-3xl space-y-3 p-5 text-[12px] leading-snug text-foreground print:p-0">
        <div className="flex items-center justify-between print:hidden">
          <Button size="sm" variant="ghost" onClick={onBack}><ArrowLeft className="mr-1 h-3.5 w-3.5" /> Back to the separation</Button>
          <Button size="sm" variant="outline" onClick={() => window.print()}><Printer className="mr-1 h-3.5 w-3.5" /> Print</Button>
        </div>
        <header className="border-b-2 border-[#C30C44] pb-1">
          <p className="text-[11px] font-bold tracking-wide text-[#C30C44]">FLEETLY <span className="font-normal text-muted-foreground">Driving Change</span></p>
          <h1 className="text-lg font-bold">Separation review summary: {d.driver_name}, {c.case_ref}</h1>
          <p className="text-muted-foreground">{STATUS_LABEL[c.status as keyof typeof STATUS_LABEL]} · offboarding {d.offboarding.case_ref} · printed {formatWhen(new Date().toISOString())}</p>
        </header>

        <section>
          <h2 className="font-bold text-[#C30C44]">Contract basis</h2>
          <p>{BASIS_LABEL[basis]}. {(c.basis_detail as { reason?: string } | null)?.reason ?? d.basis_live.reason}{c.basis ? ` Frozen at issue, ${formatWhen(c.issued_at)}.` : ' Not yet frozen.'}</p>
        </section>

        <section>
          <h2 className="font-bold text-[#C30C44]">The allegation and the request</h2>
          <p>{c.allegation ?? 'Not recorded.'} In writing on {formatDay(c.allegation_in_writing_on)}; first put on {formatDay(c.allegation_first_put_on)}.
            Right to a hearing given {formatDay(c.hearing_right_notice_on)} by {c.hearing_right_notice_method ?? 'not recorded'}.
            Separation first raised by {c.raised_by === 'employee' ? 'the driver' : c.raised_by === 'fleetly' ? 'Fleetly' : 'not recorded'} on {formatDay(c.first_conversation_on)} with {c.first_conversation_present ?? 'not recorded'} present
            {c.raised_in_allegation_meeting === true ? ', in the meeting where the allegation was first put' : ''}. Explained in {c.language ?? 'not recorded'}.
            {d.suspension.suspended ? ` Suspended ${formatDay(d.suspension.suspended_on)}; hearing by ${formatDay(d.suspension.hearing_by)}, set for ${formatDay(d.suspension.hearing_date ?? null)}.` : ' Not suspended.'}
            {c.ui19_code ? ` UI-19 reason: code ${c.ui19_code}, decided by ${d.people.ui19_decided_by}; driver told ${formatDay(c.ui19_told_on)}.` : ' UI-19 reason not decided.'}</p>
        </section>

        {gates && (
          <section>
            <h2 className="font-bold text-[#C30C44]">{c.status === 'preparing' ? 'Issue gates' : 'Signature gates'}</h2>
            <table className="w-full">
              <tbody>{gates.map((g) => (
                <tr key={g.key} className="border-t border-border align-top"><td className={`w-12 py-0.5 font-bold ${g.ok ? 'text-emerald-700' : 'text-rose-700'}`}>{g.ok ? 'Pass' : 'Fail'}</td><td>{g.text}</td></tr>
              ))}</tbody>
            </table>
          </section>
        )}

        <section>
          <h2 className="font-bold text-[#C30C44]">Figures{f ? `, as at ${formatWhen(f.as_at)}` : ''}</h2>
          {!f && <p>Not computed.</p>}
          {f && !fullFigures(f) && <p className="font-bold text-rose-700">Blocked: {(f.missing ?? []).join(' ')}</p>}
          {f && fullFigures(f) && (
            <>
              {f.status === 'blocked' && <p className="font-bold text-rose-700">Blocked: {f.missing.join(' ')}</p>}
              <table className="w-full">
                <tbody>
                  {f.lines.map((l) => (
                    <tr key={l.key} className="border-t border-border align-top">
                      <td className="w-44 py-0.5 font-medium">{LINE_LABEL[l.key] ?? l.key}</td>
                      <td className="text-muted-foreground">{l.period}. {l.basis} Source: {l.source}</td>
                      <td className="w-24 whitespace-nowrap text-right">{l.kind === 'deduction' ? '-' : ''}{lineAmount(l)}</td>
                    </tr>
                  ))}
                  {f.annexure_a.items.map((a, i) => (
                    <tr key={`a${i}`} className="border-t border-border align-top">
                      <td className="py-0.5 font-medium">Annexure A: {a.description}</td>
                      <td className="text-muted-foreground">{a.source}{a.held_back > 0 ? `; ${formatZar(a.held_back)} held back by the cap or floor` : ''}</td>
                      <td className="text-right">-{formatZar(a.allowed)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-foreground font-bold"><td className="py-0.5">Net estimate</td><td>Gross {formatZar(f.totals.earnings)}, deductions {formatZar(f.totals.deductions)}; pay by {formatDay(f.pay_by)}</td><td className="text-right">{formatZar(f.totals.net_estimate)}</td></tr>
                </tbody>
              </table>
              <p className="mt-1 text-muted-foreground">
                Deductions: 25 percent of {formatZar(f.totals.remuneration_in_money)} remuneration in money is {formatZar(f.annexure_a.cap)}; {formatZar(f.annexure_a.ranked_ahead)} ranks ahead;
                National Minimum Wage floor {formatZar(f.annexure_a.floor)} for {f.totals.paid_hours} hours; Annexure A total {formatZar(f.annexure_a.total)}.
                {f.excluded.length ? ` Not deducted: ${f.excluded.map((x) => `${x.source} ${formatZar(x.amount)} (${x.reason})`).join('; ')}.` : ' Nothing excluded.'}
              </p>
            </>
          )}
        </section>

        <section>
          <h2 className="font-bold text-[#C30C44]">Steps</h2>
          <table className="w-full">
            <tbody>{d.reviews.map((r) => (
              <tr key={r.step} className="border-t border-border align-top">
                <td className="w-56 py-0.5 font-medium">{r.step}. {r.title}</td>
                <td>{r.status === 'approved' ? `Approved by ${r.decided_by}, ${formatWhen(r.decided_at)}` : r.status === 'returned' ? `Returned: ${r.note ?? ''}` : `Pending with ${r.assignee ?? 'nobody'}${r.due_on ? `, due ${formatDay(r.due_on)}` : ''}`}</td>
              </tr>
            ))}</tbody>
          </table>
          {c.escalation_ack_by && <p className="mt-1">Escalation considered by {d.people.escalation_ack_by}: {c.escalation_ack_note}</p>}
          {c.delivered_at && <p className="mt-1">Delivered unsigned {formatWhen(c.delivered_at)} by {d.people.delivered_by} ({c.delivered_method === 'in_person' ? 'in person' : c.delivered_method}).</p>}
        </section>
      </div>
    </div>
  )
}
